import { Type, type Static } from "@fastify/type-provider-typebox";

//
// Checkout
//
export const BillingCheckoutBody = Type.Object({
  firstName: Type.String(),
  lastName: Type.String(),
  recurlyTokenId: Type.String(),
  planCode: Type.String(),
});
export type BillingCheckoutBody = Static<typeof BillingCheckoutBody>;

//
// Get Subscription
//
export const SubscriptionPlan = Type.Object({
  subscriptionId: Type.String(),
  planCode: Type.String(),
  planName: Type.Optional(Type.String()),
  isAnnual: Type.Optional(Type.Boolean()),
});
export type SubscriptionPlan = Static<typeof SubscriptionPlan>;

export const BillingGetSubscriptionResponse = Type.Object({
  subscription: Type.Optional(SubscriptionPlan),
  accountManagementUrl: Type.String(),
});
export type BillingGetSubscriptionResponse = Static<typeof BillingGetSubscriptionResponse>;

export const ChangeSubscriptionBody = Type.Object({
  subscriptionId: Type.String(),
  newPlanName: Type.Optional(Type.String()),
  //isAnnual: Type.Boolean(),
});
export type ChangeSubscriptionBody = Static<typeof ChangeSubscriptionBody>;

export const ChangeSubscriptionResponse = Type.Object({
  subscription: SubscriptionPlan
});
export type ChangeSubscriptionResponse = Static<typeof ChangeSubscriptionResponse>;

export const SubscriptionChangePreviewResponse = Type.Object({
  charge: Type.Optional(Type.Object({
    type: Type.String(),
    discount: Type.Number(),
    subtotal: Type.Number(),
    tax: Type.Number(),
    total: Type.Number(),
    refundable: Type.Number(),
  })),
  credit: Type.Optional(Type.Object({
    type: Type.String(),
    discount: Type.Number(),
    subtotal: Type.Number(),
    tax: Type.Number(),
    total: Type.Number(),
    refundable: Type.Number(),
  }))
});
export type SubscriptionChangePreviewResponse = Static<typeof SubscriptionChangePreviewResponse>;


export enum CancellationReasons {
  MISSING_FEATURES = "MISSING_FEATURES",
  NOT_USING_ENOUGH = "NOT_USING_ENOUGH",
  TECHNICAL_ISSUES = "TECHNICAL_ISSUES",
  DIFFICULT_TO_USE = "DIFFICULT_TO_USE",
  WORKFLOW_INCOMPATIBLE = "WORKFLOW_INCOMPATIBLE",
  TOO_EXPENSIVE = "TOO_EXPENSIVE",
  TEMPORARY_NEED = "TEMPORARY_NEED",
  OTHER = "OTHER"
}

// Create a TypeBox schema from the enum
export const CancellationReasonSchema = Type.Enum(CancellationReasons);

// Type that can be used in TypeScript
export type CancellationReasonType = Static<typeof CancellationReasonSchema>;

export const CancelReasonsBody = Type.Object({
  subscriptionId: Type.String(),
  reason: Type.Enum(CancellationReasons),
  feedback: Type.Optional(Type.String()),
});

export type CancelReasonsBody = Static<typeof CancelReasonsBody>;

export const CancelReasonsResponse = Type.Object({
  updatedUserId: Type.Number(),
});

export type CancelReasonsResponse = Static<typeof CancelReasonsResponse>;

export const Invoice = Type.Object({
  id: Type.String(),
  number: Type.String(),
  state: Type.String(),
  total: Type.Number(),
  currency: Type.String(),
  download_url: Type.String(),
  createdAt: Type.Optional(Type.String()),
  dueAt: Type.Optional(Type.String()),
  issuedOn: Type.Optional(Type.String()),
  dueOn: Type.Optional(Type.String()),
});

export type Invoice = Static<typeof Invoice>;

export const BillingGetInvoicesResponse = Type.Object({
  invoices: Type.Array(Invoice),
});

export type BillingGetInvoicesResponse  = Static<typeof BillingGetInvoicesResponse >;


export enum IntervalUnit {
  MONTHLY = "monthly",
  ANNUALLY = "annually",
}


export enum TermBehavior {
  AUTO_RENEW = "auto_renew",
  CANCEL_AT_PERIOD_END = "cancel_at_period_end",
}

export const SubscriptionDetail = Type.Object({
  id: Type.String(),
  plan: Type.Object({
    code: Type.String(),
    name: Type.String(),
    interval_unit: Type.Enum(IntervalUnit),
    is_there_next_tier: Type.Boolean(),
  }),
  state: Type.String(),


  current_period_started_at: Type.String(),
  current_period_ends_at: Type.String(),
  term_behavior: Type.Enum(TermBehavior),
  collection: Type.String(),
  renews_on: Type.Optional(Type.String()),
  started_at: Type.String(),
  auto_renew: Type.Boolean(),
  cancelled_at: Type.Optional(Type.String()),

  latestInvoice: Type.String(),


  nextInvoice: Type.Object({
    date: Type.String(),
    amount: Type.Number(),
  }),
  currency: Type.String(),
  invoices: Type.Array(Invoice)
});

export type SubscriptionDetail = Static<typeof SubscriptionDetail>;

export enum CreditCardType {
  VISA = "Visa",
  MASTERCARD = "Mastercard",
  AMERICAN_EXPRESS = "American_express",
  DISCOVER = "Discover",
}

export const PaymentMethodDetail = Type.Object({
  id: Type.String(),
  name: Type.String(),
  creditCardLastFour: Type.String(),
  creditCardExpiration: Type.String(),
  creditCardType: Type.Enum(CreditCardType),
  address: Type.String(),
  city: Type.String(),
  state: Type.String(),
  zipCode: Type.String(),
  country: Type.String(),
});

export type PaymentMethodDetail = Static<typeof PaymentMethodDetail>;





// for invoice detial

// For enum values that might be needed
const InvoiceTerms = Type.Union([
  Type.Literal('On-receipt'),
  Type.Literal('Net 30'),
  Type.Literal('Net 60'),
  // Add other possible terms as needed
]);

// Address type for reuse
const Address = Type.Object({
  street1: Type.String(),
  street2: Type.Optional(Type.String()),
  city: Type.String(),
  region: Type.String(),
  postalCode: Type.String(),
  country: Type.String(),
});

// Line item type
const InvoiceLineItem = Type.Object({
  date: Type.String(), // "Mar 25 - Apr 25, 2025"
  description: Type.String(), // "Professional - Launch Special"
  quantity: Type.Number(), // 1
  price: Type.Number(), // 69.99
  discount: Type.Number(), // -69.99
  total: Type.Number(), // 0.00
});

// Main invoice type
export const InvoiceDetail = Type.Object({
  // Company Info
  companyName: Type.String(), // "Seven Tools LLC"
  companyAddress: Address,
  companyEmail: Type.String(), // "support@boxem.com"
  
  // Invoice Details
  invoiceNumber: Type.String(), // "10416"
  billedOn: Type.String(), // "Mar 25, 2025"
  terms: InvoiceTerms, // "On-receipt"
  dueOn: Type.String(), // "Mar 25, 2025"
  
  // Bill To
  customerName: Type.String(), // "Michael Auderer"
  customerAddress: Address,
  
  // Line Items
  lineItems: Type.Array(InvoiceLineItem),
  
  // Totals
  subtotal: Type.Number(), // 0.00
  total: Type.Number(), // 0.00
  paid: Type.Number(), // 0.00
  amountDue: Type.Number(), // 0.00
  currency: Type.String(), // "USD"
  
  // Notes
  discountApplied: Type.String(), // "100% off"
});

// Export the type
export type InvoiceDetail = Static<typeof InvoiceDetail>;



//Update payment method
export const BillingUpdatePaymentMethodBody = Type.Object({
  recurlyTokenId: Type.String(),
  billingInfoId: Type.String(),
});
export type BillingUpdatePaymentMethodBody  = Static<typeof BillingUpdatePaymentMethodBody >;


export enum PaymentMethodUpdateErrorType {
  PAYMENT_DECLINED = "payment_declined",
  INTERNAL_SERVER_ERROR = "internal_server_error",
}

export const PaymentMethodUpdateErrorResponse = Type.Object({
  status: Type.Number(),
  message: Type.Optional(Type.String()),
  type: Type.Optional(Type.Enum(PaymentMethodUpdateErrorType)),
  additionalMessage: Type.Optional(Type.String()),
});

export type PaymentMethodUpdateErrorResponse = Static<typeof PaymentMethodUpdateErrorResponse>;